import React from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Leaf } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, Kpi } from "@/components/common";
import { num } from "@/lib/format";

export default function Sustainability() {
  const { data } = usePoll(endpoints.sustainability, 5000);
  if (!data) return <Loading />;
  const m = data.metrics;
  const trend = (data.trend || []).map((d) => ({ ...d, t: new Date(d.t).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }) }));
  const sources = data.twin.sources || [];

  return (
    <div data-testid="sustainability-page">
      <PageHeader icon={Leaf} title="Sustainability Center"
        subtitle="Sustainability as an outcome of operational excellence — energy, water & carbon intensity." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi testid="sus-energy" label="Energy Intensity" value={m.energy_intensity_kwh_l} unit="kWh/L" accent="#2563eb" />
        <Kpi testid="sus-water" label="Water Intensity" value={m.water_intensity_l_l} unit="L/L" accent="#06b6d4" />
        <Kpi testid="sus-carbon" label="Carbon Intensity" value={m.carbon_intensity_kg_l} unit="kgCO₂/L" accent="#059669" />
        <Kpi testid="sus-renewable" label="Renewable Share" value={m.renewable_pct} unit="%" accent="#10b981" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel testid="panel-emission-sources" title="Emission Sources" subtitle="kg CO₂ per hour">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sources} margin={{ left: 0, right: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => num(v) + " kg/hr"} />
              <Bar dataKey="co2_per_hr" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {sources.map((s, i) => <Cell key={i} fill={i === 0 ? "#8b5cf6" : "#f59e0b"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-carbon-trend" title="Carbon & Energy Intensity Trend" subtitle="Live (last ~30 min)" className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
              <Tooltip />
              <Line type="linear" dataKey="carbon_l" name="Carbon kgCO₂/L" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="linear" dataKey="energy_l" name="Energy kWh/L" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel testid="panel-carbon-opportunity" title="Carbon Reduction Opportunities">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {[
            { t: "Boiler fuel switch to biomass", v: "−1,450 tCO₂/yr" },
            { t: "Solar PV expansion to 50%", v: "−2,100 tCO₂/yr" },
            { t: "Condensate & flash-steam recovery", v: "−620 tCO₂/yr" },
          ].map((o, i) => (
            <div key={i} className="border border-slate-100 rounded p-3 flex items-center justify-between">
              <span className="text-slate-700">{o.t}</span>
              <span className="font-mono font-semibold text-emerald-600">{o.v}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
